import { Button, Caption1, makeStyles, Subtitle1, tokens } from "@fluentui/react-components";
import { useEffect, useState } from "react";
import { loadSession } from "../api/client";
import type { ReceivedUnit } from "../api/commerce";
import { baeTypography } from "../theme/baeTheme";

const useStyles = makeStyles({
  bar: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalM,
  },
  heading: { fontFamily: baeTypography.fontFamilyDisplay, letterSpacing: "-0.02em", margin: 0 },
  spacer: { flexGrow: 1 },
  sheet: {
    display: "grid",
    // 50mm labels, sized in physical units so what prints matches what a thermal roll expects.
    gridTemplateColumns: "repeat(auto-fill, 50mm)",
    // A label is a fixed physical size, so on a narrow screen the sheet is the thing that scrolls
    // rather than the page around it.
    maxWidth: "100%",
    overflowX: "auto",
    gap: "4mm",
    padding: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
  },
  label: {
    width: "50mm",
    height: "25mm",
    backgroundColor: "#FFFFFF",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    // Kept inside its own box so a browser cannot reflow a label across a page break.
    breakInside: "avoid",
    pageBreakInside: "avoid",
  },
  empty: { color: tokens.colorNeutralForeground3 },
});

type Props = { units: ReceivedUnit[]; onDone: () => void };

/**
 * The printable sheet for a receipt's labels.
 *
 * Each label is fetched as SVG from the API and shown at its true physical size, so what appears
 * on screen is what comes out of the printer. The print stylesheet in index.css hides everything
 * except this sheet, which is why "Print" produces labels rather than a screenshot of the app.
 */
export function LabelSheet({ units, onDone }: Props) {
  const styles = useStyles();
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [failed, setFailed] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const session = loadSession();
    const base = import.meta.env.VITE_API_BASE_URL ?? "http://localhost:5199";

    // Fetched rather than dropped into an <img src>, because the label endpoint needs the bearer
    // token and an img tag cannot send one.
    const load = async () => {
      const results: Record<string, string> = {};
      let errors = 0;

      for (const unit of units) {
        try {
          const response = await fetch(base + unit.labelUrl, {
            headers: session ? { Authorization: `Bearer ${session.accessToken}` } : {},
          });

          if (!response.ok) {
            errors += 1;
            continue;
          }

          results[unit.stockUnitId] = await response.text();
        } catch {
          errors += 1;
        }
      }

      if (!cancelled) {
        setLabels(results);
        setFailed(errors);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [units]);

  const ready = Object.keys(labels).length;

  return (
    <div>
      <div className={`${styles.bar} no-print`}>
        <Subtitle1 className={styles.heading} as="h2">
          Labels
        </Subtitle1>
        <Caption1>
          {ready} of {units.length} ready
          {failed > 0 ? ` · ${failed} could not be loaded` : ""}
        </Caption1>
        <div className={styles.spacer} />
        <Button appearance="secondary" onClick={onDone}>
          Back
        </Button>
        <Button appearance="primary" disabled={ready === 0} onClick={() => window.print()}>
          Print {ready} labels
        </Button>
      </div>

      {ready === 0 ? (
        <Caption1 className={styles.empty}>Loading labels…</Caption1>
      ) : (
        <div className={`${styles.sheet} print-area`}>
          {units.map((unit) => {
            const svg = labels[unit.stockUnitId];
            if (!svg) return null;

            return (
              <div key={unit.stockUnitId} className={styles.label}>
                <img
                  src={`data:image/svg+xml;base64,${btoa(svg)}`}
                  alt={`Label for ${unit.serial}`}
                  width="100%"
                  height="100%"
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
